/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as MyTv from 'store/ducks/myTv';
import { Rail as RailComponent, SimpleHeader } from 'ui/components';
import translate from 'services/i18n/translate';
import Constants from 'configs/Constants';
import { PrivateRoutePath } from 'routes/paths';
import RouterUtils from 'services/utils/RouterUtils';
import { Asset, Ducks, EntryPoint, MenuItemType, MenuUiArea, StoreState } from '@wit/vtv-x-foundation-lib';
import { useHistoryWithState } from 'hooks/useHistoryWithState';
import { useRouter } from '../../../hooks/useRouter';

interface UriParams {
  menuItemId: string;
}

export const getRightSubtitle = (menuItemType: string | undefined): string => {
  switch (menuItemType) {
    case MenuItemType.WISHLIST:
      return translate['category.wishlist'];
    case MenuItemType.RENTALS:
      return translate['firstTimeUse.purchases.label'];
    case MenuItemType.RECORDINGS:
      return translate['category.current_recordings'];
    case MenuItemType.SCHEDULE_RECORDINGS:
      return translate['firstTimeUse.scheduledRecordings.label'];
    default:
      return menuItemType || '';
  }
};

export const getEntryPoint = (menuItemType: string | undefined): EntryPoint | undefined => {
  switch (menuItemType) {
    case MenuItemType.WISHLIST:
      return EntryPoint.WISHLIST;
    case MenuItemType.RECORDINGS:
      return EntryPoint.RECORDINGS;
    case MenuItemType.SCHEDULE_RECORDINGS:
      return EntryPoint.SCHEDULED_RECORDINGS;
    case MenuItemType.RENTALS:
      return EntryPoint.PURCHASES;
    default:
      return undefined;
  }
};

export default function GenreRailsLevel() {
  const history = useHistoryWithState();
  const router = useRouter();
  const { menuItemId: menuItemIdString } = useParams<UriParams>();
  const menuItemId = useMemo(() => Number.parseInt(menuItemIdString, 10) || 0, [menuItemIdString]);
  const menuItem = useSelector((store: StoreState) =>
    Ducks.Menu.Selectors.getMenuItemByAreaAndId(store, MenuUiArea.MY_TV, menuItemId)
  );
  const dispatch = useDispatch();
  const isRecordingsProcessed = useSelector(MyTv.Selectors.isRecordingsProcessed);

  useEffect(() => {
    dispatch(MyTv.ActionCreators.getSecondLevelAssets(menuItemId));
  }, [dispatch, menuItemId, isRecordingsProcessed]);

  const onHeaderClick = (railId: number | string) => {
    history.push(RouterUtils.getRouteWithDynamicValues(PrivateRoutePath.MYTV_RAIL_CATEGORY, [menuItemId, railId]));
  };

  const onCardClick = (asset: Asset, menuItemId_?: number) => {
    router.push({ asset, entryPoint: getEntryPoint(menuItem?.type), menuItemId: menuItemId_ });
  };

  const rails = useSelector(MyTv.Selectors.getSecondLevelAssets);

  const backUrl = RouterUtils.getRouteWithDynamicValues(PrivateRoutePath.MYTV, []);

  useEffect(
    function goToPreviousLevelIfNotEnoughAssets() {
      if (rails?.length) {
        const totalAssets = rails.reduce((acc, rail) => acc + rail.assets.length, 0);
        if (totalAssets <= Constants.MAX_ASSETS_PER_RAIL) {
          history.replace(backUrl);
        }
      }
    },
    [backUrl, history, rails]
  );
  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);
  return (
    <div className="ondemand-nextlevel template-holder back-nav" id="MyTv_genreRails">
      {menuItemId ? (
        <SimpleHeader subtitle={getRightSubtitle(menuItem?.type)} hideBackIcon />
      ) : (
        <SimpleHeader
          subtitle={getRightSubtitle(menuItem?.type)}
          backLabel={translate['entrypoint.wishlist']}
          backUrl={backUrl}
        />
      )}

      {rails?.map((rail, index) => {
        const key = `MyTv_genreRails___${index}`;
        return (
          <RailComponent
            railId={rail.railId}
            menuItemId={rail.menuItemId}
            key={key}
            className="no-background"
            railName={rail.railName}
            assets={rail.assets}
            hasMore={rail.assets.length > Constants.MAX_ASSETS_PER_RAIL}
            onHeaderClick={onHeaderClick}
            onCardClick={onCardClick}
          />
        );
      })}
    </div>
  );
}
